import 'src/styles/ui/resource-item.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * ResourceItem — flexible list-item card for feed/history-style entries
 * (events, records, windows). Unlike ResourceCard (grey header + body), this is
 * a single flat card: a leading visual (DateTile or icon), a wrapping title
 * with a status pinned top-right, muted meta lines, a chips row, and an
 * optional divided actions row.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.leading - Leading visual (e.g. <DateTile/>); mutually exclusive with `icon`
 * @param {Object} props.icon - FontAwesome icon rendered in a soft tile (fallback leading visual)
 * @param {React.ReactNode} props.title - Item title (wraps; never pushes the status)
 * @param {React.ReactNode} props.status - Status pill pinned top-right (e.g. <StatusBadge/>)
 * @param {React.ReactNode} props.meta - Meta line(s) under the title (icon + text)
 * @param {React.ReactNode} props.chips - Chips row (small badges)
 * @param {React.ReactNode} props.actions - Buttons row, divided from the content
 * @param {Function} props.onClick - Makes the whole item tappable
 */
const ResourceItem = ({
  leading,
  icon,
  title,
  status,
  meta,
  chips,
  actions,
  onClick,
  className,
  ...props
}) => {
  const tapProps = onClick
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: event => {
          if (event.key === 'Enter' || event.key === ' ') onClick(event);
        },
      }
    : {};

  return (
    <div
      className={classNames('resource-item', { 'resource-item--tappable': !!onClick }, className)}
      {...tapProps}
      {...props}
    >
      <div className='resource-item__head'>
        {(leading || icon) && (
          <div className='resource-item__leading'>
            {leading || (
              <div className='resource-item__icon-tile'>
                <FontAwesomeIcon icon={icon} />
              </div>
            )}
          </div>
        )}
        <div className='resource-item__body'>
          <div className='resource-item__title-row'>
            <div className='resource-item__title'>{title}</div>
            {status && <div className='resource-item__status'>{status}</div>}
          </div>
          {meta && <div className='resource-item__meta'>{meta}</div>}
          {chips && <div className='resource-item__chips'>{chips}</div>}
        </div>
      </div>
      {actions && <div className='resource-item__actions'>{actions}</div>}
    </div>
  );
};

ResourceItem.propTypes = {
  leading: PropTypes.node,
  icon: PropTypes.object,
  title: PropTypes.node.isRequired,
  status: PropTypes.node,
  meta: PropTypes.node,
  chips: PropTypes.node,
  actions: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

ResourceItem.defaultProps = {
  leading: null,
  icon: null,
  status: null,
  meta: null,
  chips: null,
  actions: null,
  onClick: null,
  className: '',
};

export default ResourceItem;
